export const SUPPORTED_LANGUAGES = ['en', 'es', 'fr', 'de', 'zh', 'it', 'pt', 'nl', 'pl', 'ru'] as const;
export type LanguageCode = (typeof SUPPORTED_LANGUAGES)[number];

export const LANGUAGE_OPTIONS: { code: LanguageCode; name: string }[] = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Spanish (Español)' },
  { code: 'fr', name: 'French (Français)' },
  { code: 'de', name: 'German (Deutsch)' },
  { code: 'zh', name: 'Chinese (中文)' },
  { code: 'it', name: 'Italian (Italiano)' },
  { code: 'pt', name: 'Portuguese (Português)' },
  { code: 'nl', name: 'Dutch (Nederlands)' },
  { code: 'pl', name: 'Polish (Polski)' },
  { code: 'ru', name: 'Russian (Русский)' },
];

export const LANGUAGE_STORAGE_KEY = 'adminPortalLanguage';
export const DISPLAY_SETTINGS_KEY = 'adminDisplaySettings';

type Dict = Record<string, string>;

const es: Dict = {
  'Admin Dashboard': 'Panel de administración',
  Dashboard: 'Panel',
  'Purchase Order Received': 'Pedidos recibidos',
  'Purchase Order Sent': 'Pedidos enviados',
  Shipments: 'Envíos',
  'Claims Received': 'Reclamaciones recibidas',
  'Claims Created': 'Reclamaciones creadas',
  Inventory: 'Inventario',
  'Parts Hierarchy': 'Jerarquía de piezas',
  'PartFinder Intelligence': 'Inteligencia PartFinder',
  'OEM PMS Library': 'Biblioteca PMS OEM',
  Sales: 'Ventas',
  Purchases: 'Compras',
  Users: 'Usuarios',
  Customers: 'Clientes',
  Support: 'Soporte',
  Organizations: 'Organizaciones',
  Agreements: 'Acuerdos',
  'Database Management': 'Gestión de datos',
  'Vessel Management': 'Gestión de buques',
  'Port Management': 'Gestión de puertos',
  'Classification Societies': 'Sociedades de clasificación',
  'Classification Surveyors': 'Inspectores de clasificación',
  'Data Seeding': 'Carga de datos',
  Licenses: 'Licencias',
  'All Licenses': 'Todas las licencias',
  'Upgrade Requests': 'Solicitudes de mejora',
  'Risk & Incidents': 'Riesgos e incidentes',
  'Risk Management': 'Gestión de riesgos',
  'Role Management': 'Gestión de roles',
  'Roles & Permissions': 'Roles y permisos',
  'Assign Roles': 'Asignar roles',
  'Support Tickets': 'Tickets de soporte',
  'Customer Support': 'Atención al cliente',
  Analytics: 'Analítica',
  Settings: 'Configuración',
  Logout: 'Cerrar sesión',
  'Search modules (RFQs, users, licenses...)': 'Buscar módulos (RFQ, usuarios, licencias...)',
  Search: 'Buscar',
  'No matching modules. Press Enter to search RFQs.': 'Sin coincidencias. Pulse Intro para buscar RFQ.',
  'Refresh page': 'Actualizar página',
  Messages: 'Mensajes',
  Notifications: 'Notificaciones',
  'Latest Notifications': 'Últimas notificaciones',
  All: 'Todas',
  Onboarding: 'Alta',
  'Loading notifications...': 'Cargando notificaciones...',
  'No new notifications': 'No hay notificaciones nuevas',
  "You're all caught up.": 'Está al día.',
  Close: 'Cerrar',
  'Mark all as read': 'Marcar todo como leído',
  Profile: 'Perfil',
  'Change/Reset Password': 'Cambiar/restablecer contraseña',
  'Language Selection': 'Selección de idioma',
  'Time Zone': 'Zona horaria',
  'Date and Time Format': 'Formato de fecha y hora',
  'Help & Docs': 'Ayuda y documentos',
  'Toggle menu': 'Mostrar menú',
  Admin: 'Admin',
  'Manage your account settings, preferences, and security options':
    'Gestione la configuración, preferencias y seguridad de su cuenta',
  Display: 'Pantalla',
  Security: 'Seguridad',
  Procurement: 'Compras',
  'Banking Details': 'Datos bancarios',
  'Display Preferences': 'Preferencias de visualización',
  'Customize how information is displayed in your portal':
    'Personalice cómo se muestra la información en el portal',
  Theme: 'Tema',
  Light: 'Claro',
  Dark: 'Oscuro',
  'Auto (System)': 'Automático (sistema)',
  Language: 'Idioma',
  English: 'Inglés',
  Spanish: 'Español',
  French: 'Francés',
  German: 'Alemán',
  Chinese: 'Chino',
  'Date Format': 'Formato de fecha',
  'Time Format': 'Formato de hora',
  '12 Hour (AM/PM)': '12 horas (AM/PM)',
  '24 Hour': '24 horas',
  Currency: 'Moneda',
  'Enable Shipment Map': 'Activar mapa de envíos',
  'Display the interactive tracking map on shipment detail pages':
    'Mostrar el mapa de seguimiento en las páginas de envío',
  'Apply Display Preferences': 'Aplicar preferencias',
  'Display preferences applied': 'Preferencias de visualización aplicadas',
  'Display currency updated across the portal': 'Moneda actualizada en todo el portal',
  'Language updated across the portal': 'Idioma actualizado en todo el portal',
  'Total RFQs Received': 'RFQ recibidas',
  'Total Merchants': 'Comerciantes',
  'Total Happy Customers': 'Clientes',
  'Total Brands': 'Marcas',
  'Total Items Send': 'Artículos enviados',
  'Total Categories': 'Categorías',
  'Updated Now': 'Actualizado ahora',
  'Invite Admin': 'Invitar administrador',
  'Bar Chart - Multiple': 'Gráfico de barras',
  'Area Chart - Gradient': 'Gráfico de área',
  'Showing RFQs for the last 6 months': 'RFQ de los últimos 6 meses',
  'Showing total visitors for the last 6 months': 'Visitantes de los últimos 6 meses',
  'Admin Portal': 'Portal de administración',
  'Admin Sign In': 'Inicio de sesión admin',
  'Access the administration dashboard': 'Acceda al panel de administración',
  Email: 'Correo electrónico',
  Password: 'Contraseña',
  'Forgot password?': '¿Olvidó su contraseña?',
  'Sign In': 'Iniciar sesión',
  'Signing in...': 'Iniciando sesión...',
  'Choose your preferred language for the interface':
    'Elija el idioma preferido de la interfaz',
  'Select Language': 'Seleccionar idioma',
  'Save Language': 'Guardar idioma',
  'Language preference saved successfully': 'Idioma guardado correctamente',
  Saving: 'Guardando',
  'Sales · RFQ': 'Ventas · RFQ',
  'Sales · Offers': 'Ventas · Ofertas',
  'Sales · Purchase Orders': 'Ventas · Pedidos',
  'Sales · In Progress': 'Ventas · En curso',
  'Sales · Order History': 'Ventas · Historial de pedidos',
  'Sales · Claims': 'Ventas · Reclamaciones',
  'Purchases · RFQ': 'Compras · RFQ',
  'Purchases · Offers': 'Compras · Ofertas',
  'Purchases · Purchase Orders': 'Compras · Pedidos',
  'Purchases · In Progress': 'Compras · En curso',
  'Purchases · Order History': 'Compras · Historial de pedidos',
  'Purchases · Claims': 'Compras · Reclamaciones',
  'Vessel Database': 'Base de buques',
  'Data Management': 'Gestión de datos',
};

const fr: Dict = {
  'Admin Dashboard': 'Tableau de bord admin',
  Dashboard: 'Tableau de bord',
  'Purchase Order Received': 'Commandes reçues',
  'Purchase Order Sent': 'Commandes envoyées',
  Shipments: 'Expéditions',
  'Claims Received': 'Réclamations reçues',
  'Claims Created': 'Réclamations créées',
  Inventory: 'Inventaire',
  'Parts Hierarchy': 'Hiérarchie des pièces',
  'PartFinder Intelligence': 'Intelligence PartFinder',
  'OEM PMS Library': 'Bibliothèque PMS OEM',
  Sales: 'Ventes',
  Purchases: 'Achats',
  Users: 'Utilisateurs',
  Customers: 'Clients',
  Support: 'Support',
  Organizations: 'Organisations',
  Agreements: 'Accords',
  'Database Management': 'Gestion des données',
  'Vessel Management': 'Gestion des navires',
  'Port Management': 'Gestion des ports',
  'Classification Societies': 'Sociétés de classification',
  'Classification Surveyors': 'Experts de classification',
  'Data Seeding': 'Alimentation des données',
  Licenses: 'Licences',
  'All Licenses': 'Toutes les licences',
  'Upgrade Requests': 'Demandes de mise à niveau',
  'Risk & Incidents': 'Risques et incidents',
  'Risk Management': 'Gestion des risques',
  'Role Management': 'Gestion des rôles',
  'Roles & Permissions': 'Rôles et permissions',
  'Assign Roles': 'Attribuer des rôles',
  'Support Tickets': 'Tickets de support',
  'Customer Support': 'Support client',
  Analytics: 'Analytique',
  Settings: 'Paramètres',
  Logout: 'Déconnexion',
  'Search modules (RFQs, users, licenses...)': 'Rechercher des modules (RFQ, utilisateurs, licences...)',
  Search: 'Rechercher',
  'No matching modules. Press Enter to search RFQs.': 'Aucun module. Appuyez sur Entrée pour chercher des RFQ.',
  'Refresh page': 'Actualiser la page',
  Messages: 'Messages',
  Notifications: 'Notifications',
  'Latest Notifications': 'Dernières notifications',
  All: 'Tout',
  Onboarding: 'Intégration',
  'Loading notifications...': 'Chargement des notifications...',
  'No new notifications': 'Aucune nouvelle notification',
  "You're all caught up.": 'Vous êtes à jour.',
  Close: 'Fermer',
  'Mark all as read': 'Tout marquer comme lu',
  Profile: 'Profil',
  'Change/Reset Password': 'Changer/réinitialiser le mot de passe',
  'Language Selection': 'Sélection de la langue',
  'Time Zone': 'Fuseau horaire',
  'Date and Time Format': 'Format de date et heure',
  'Help & Docs': 'Aide et docs',
  'Toggle menu': 'Afficher le menu',
  Admin: 'Admin',
  'Manage your account settings, preferences, and security options':
    'Gérez les paramètres, préférences et options de sécurité',
  Display: 'Affichage',
  Security: 'Sécurité',
  Procurement: 'Approvisionnement',
  'Banking Details': 'Coordonnées bancaires',
  'Display Preferences': "Préférences d'affichage",
  'Customize how information is displayed in your portal':
    "Personnalisez l'affichage des informations dans le portail",
  Theme: 'Thème',
  Light: 'Clair',
  Dark: 'Sombre',
  'Auto (System)': 'Auto (système)',
  Language: 'Langue',
  English: 'Anglais',
  Spanish: 'Espagnol',
  French: 'Français',
  German: 'Allemand',
  Chinese: 'Chinois',
  'Date Format': 'Format de date',
  'Time Format': "Format de l'heure",
  '12 Hour (AM/PM)': '12 heures (AM/PM)',
  '24 Hour': '24 heures',
  Currency: 'Devise',
  'Enable Shipment Map': "Activer la carte d'expédition",
  'Display the interactive tracking map on shipment detail pages':
    'Afficher la carte de suivi sur les pages d’expédition',
  'Apply Display Preferences': 'Appliquer les préférences',
  'Display preferences applied': "Préférences d'affichage appliquées",
  'Display currency updated across the portal': 'Devise mise à jour dans tout le portail',
  'Language updated across the portal': 'Langue mise à jour dans tout le portail',
  'Total RFQs Received': 'RFQ reçues',
  'Total Merchants': 'Marchands',
  'Total Happy Customers': 'Clients',
  'Total Brands': 'Marques',
  'Total Items Send': 'Articles envoyés',
  'Total Categories': 'Catégories',
  'Updated Now': 'Mis à jour',
  'Invite Admin': 'Inviter un admin',
  'Bar Chart - Multiple': 'Graphique à barres',
  'Area Chart - Gradient': 'Graphique en aires',
  'Showing RFQs for the last 6 months': 'RFQ des 6 derniers mois',
  'Showing total visitors for the last 6 months': 'Visiteurs des 6 derniers mois',
  'Admin Portal': "Portail d'administration",
  'Admin Sign In': 'Connexion admin',
  'Access the administration dashboard': "Accéder au tableau de bord d'administration",
  Email: 'E-mail',
  Password: 'Mot de passe',
  'Forgot password?': 'Mot de passe oublié ?',
  'Sign In': 'Se connecter',
  'Signing in...': 'Connexion...',
  'Choose your preferred language for the interface':
    "Choisissez la langue de l'interface",
  'Select Language': 'Sélectionner la langue',
  'Save Language': 'Enregistrer la langue',
  'Language preference saved successfully': 'Langue enregistrée',
  Saving: 'Enregistrement',
  'Sales · RFQ': 'Ventes · RFQ',
  'Sales · Offers': 'Ventes · Offres',
  'Sales · Purchase Orders': 'Ventes · Commandes',
  'Sales · In Progress': 'Ventes · En cours',
  'Sales · Order History': 'Ventes · Historique des commandes',
  'Sales · Claims': 'Ventes · Réclamations',
  'Purchases · RFQ': 'Achats · RFQ',
  'Purchases · Offers': 'Achats · Offres',
  'Purchases · Purchase Orders': 'Achats · Commandes',
  'Purchases · In Progress': 'Achats · En cours',
  'Purchases · Order History': 'Achats · Historique des commandes',
  'Purchases · Claims': 'Achats · Réclamations',
  'Vessel Database': 'Base navires',
  'Data Management': 'Gestion des données',
};

const de: Dict = {
  'Admin Dashboard': 'Admin-Dashboard',
  Dashboard: 'Dashboard',
  'Purchase Order Received': 'Eingegangene Bestellungen',
  'Purchase Order Sent': 'Gesendete Bestellungen',
  Shipments: 'Sendungen',
  'Claims Received': 'Eingegangene Reklamationen',
  'Claims Created': 'Erstellte Reklamationen',
  Inventory: 'Bestand',
  'Parts Hierarchy': 'Teilehierarchie',
  'PartFinder Intelligence': 'PartFinder Intelligence',
  'OEM PMS Library': 'OEM-PMS-Bibliothek',
  Sales: 'Vertrieb',
  Purchases: 'Einkauf',
  Users: 'Benutzer',
  Customers: 'Kunden',
  Support: 'Support',
  Organizations: 'Organisationen',
  Agreements: 'Vereinbarungen',
  'Database Management': 'Datenverwaltung',
  'Vessel Management': 'Schiffsverwaltung',
  'Port Management': 'Hafenverwaltung',
  'Classification Societies': 'Klassifikationsgesellschaften',
  'Classification Surveyors': 'Klassifikationsbesichtiger',
  'Data Seeding': 'Datenimport',
  Licenses: 'Lizenzen',
  'All Licenses': 'Alle Lizenzen',
  'Upgrade Requests': 'Upgrade-Anfragen',
  'Risk & Incidents': 'Risiken & Vorfälle',
  'Risk Management': 'Risikomanagement',
  'Role Management': 'Rollenverwaltung',
  'Roles & Permissions': 'Rollen & Berechtigungen',
  'Assign Roles': 'Rollen zuweisen',
  'Support Tickets': 'Support-Tickets',
  'Customer Support': 'Kundensupport',
  Analytics: 'Analysen',
  Settings: 'Einstellungen',
  Logout: 'Abmelden',
  'Search modules (RFQs, users, licenses...)': 'Module suchen (RFQs, Benutzer, Lizenzen...)',
  Search: 'Suchen',
  'No matching modules. Press Enter to search RFQs.': 'Keine Treffer. Eingabe drücken, um RFQs zu suchen.',
  'Refresh page': 'Seite aktualisieren',
  Messages: 'Nachrichten',
  Notifications: 'Benachrichtigungen',
  'Latest Notifications': 'Neueste Benachrichtigungen',
  All: 'Alle',
  Onboarding: 'Onboarding',
  'Loading notifications...': 'Benachrichtigungen werden geladen...',
  'No new notifications': 'Keine neuen Benachrichtigungen',
  "You're all caught up.": 'Sie sind auf dem neuesten Stand.',
  Close: 'Schließen',
  'Mark all as read': 'Alle als gelesen markieren',
  Profile: 'Profil',
  'Change/Reset Password': 'Passwort ändern/zurücksetzen',
  'Language Selection': 'Sprachauswahl',
  'Time Zone': 'Zeitzone',
  'Date and Time Format': 'Datums- und Zeitformat',
  'Help & Docs': 'Hilfe & Docs',
  'Toggle menu': 'Menü umschalten',
  Admin: 'Admin',
  'Manage your account settings, preferences, and security options':
    'Kontoeinstellungen, Präferenzen und Sicherheit verwalten',
  Display: 'Anzeige',
  Security: 'Sicherheit',
  Procurement: 'Beschaffung',
  'Banking Details': 'Bankdaten',
  'Display Preferences': 'Anzeigeeinstellungen',
  'Customize how information is displayed in your portal':
    'Legen Sie fest, wie Informationen im Portal angezeigt werden',
  Theme: 'Design',
  Light: 'Hell',
  Dark: 'Dunkel',
  'Auto (System)': 'Auto (System)',
  Language: 'Sprache',
  English: 'Englisch',
  Spanish: 'Spanisch',
  French: 'Französisch',
  German: 'Deutsch',
  Chinese: 'Chinesisch',
  'Date Format': 'Datumsformat',
  'Time Format': 'Zeitformat',
  '12 Hour (AM/PM)': '12 Stunden (AM/PM)',
  '24 Hour': '24 Stunden',
  Currency: 'Währung',
  'Enable Shipment Map': 'Sendungskarte aktivieren',
  'Display the interactive tracking map on shipment detail pages':
    'Interaktive Tracking-Karte auf Sendungsseiten anzeigen',
  'Apply Display Preferences': 'Anzeigeeinstellungen übernehmen',
  'Display preferences applied': 'Anzeigeeinstellungen übernommen',
  'Display currency updated across the portal': 'Währung im gesamten Portal aktualisiert',
  'Language updated across the portal': 'Sprache im gesamten Portal aktualisiert',
  'Total RFQs Received': 'Empfangene RFQs',
  'Total Merchants': 'Händler',
  'Total Happy Customers': 'Kunden',
  'Total Brands': 'Marken',
  'Total Items Send': 'Gesendete Artikel',
  'Total Categories': 'Kategorien',
  'Updated Now': 'Gerade aktualisiert',
  'Invite Admin': 'Admin einladen',
  'Bar Chart - Multiple': 'Balkendiagramm',
  'Area Chart - Gradient': 'Flächendiagramm',
  'Showing RFQs for the last 6 months': 'RFQs der letzten 6 Monate',
  'Showing total visitors for the last 6 months': 'Besucher der letzten 6 Monate',
  'Admin Portal': 'Admin-Portal',
  'Admin Sign In': 'Admin-Anmeldung',
  'Access the administration dashboard': 'Zum Administrations-Dashboard',
  Email: 'E-Mail',
  Password: 'Passwort',
  'Forgot password?': 'Passwort vergessen?',
  'Sign In': 'Anmelden',
  'Signing in...': 'Anmeldung...',
  'Choose your preferred language for the interface':
    'Wählen Sie die Sprache der Oberfläche',
  'Select Language': 'Sprache wählen',
  'Save Language': 'Sprache speichern',
  'Language preference saved successfully': 'Sprache gespeichert',
  Saving: 'Speichern',
  'Sales · RFQ': 'Vertrieb · RFQ',
  'Sales · Offers': 'Vertrieb · Angebote',
  'Sales · Purchase Orders': 'Vertrieb · Bestellungen',
  'Sales · In Progress': 'Vertrieb · In Bearbeitung',
  'Sales · Order History': 'Vertrieb · Bestellhistorie',
  'Sales · Claims': 'Vertrieb · Reklamationen',
  'Purchases · RFQ': 'Einkauf · RFQ',
  'Purchases · Offers': 'Einkauf · Angebote',
  'Purchases · Purchase Orders': 'Einkauf · Bestellungen',
  'Purchases · In Progress': 'Einkauf · In Bearbeitung',
  'Purchases · Order History': 'Einkauf · Bestellhistorie',
  'Purchases · Claims': 'Einkauf · Reklamationen',
  'Vessel Database': 'Schiffsdatenbank',
  'Data Management': 'Datenverwaltung',
};

const zh: Dict = {
  'Admin Dashboard': '管理仪表板',
  Dashboard: '仪表板',
  'Purchase Order Received': '已收采购订单',
  'Purchase Order Sent': '已发采购订单',
  Shipments: '货运',
  'Claims Received': '已收索赔',
  'Claims Created': '已创建索赔',
  Inventory: '库存',
  'Parts Hierarchy': '零件层级',
  'PartFinder Intelligence': 'PartFinder 智能',
  'OEM PMS Library': 'OEM PMS 库',
  Sales: '销售',
  Purchases: '采购',
  Users: '用户',
  Customers: '客户',
  Support: '支持',
  Organizations: '组织',
  Agreements: '协议',
  'Database Management': '数据管理',
  'Vessel Management': '船舶管理',
  'Port Management': '港口管理',
  'Classification Societies': '船级社',
  'Classification Surveyors': '船级社验船师',
  'Data Seeding': '数据导入',
  Licenses: '许可证',
  'All Licenses': '全部许可证',
  'Upgrade Requests': '升级申请',
  'Risk & Incidents': '风险与事件',
  'Risk Management': '风险管理',
  'Role Management': '角色管理',
  'Roles & Permissions': '角色与权限',
  'Assign Roles': '分配角色',
  'Support Tickets': '支持工单',
  'Customer Support': '客户支持',
  Analytics: '分析',
  Settings: '设置',
  Logout: '退出登录',
  'Search modules (RFQs, users, licenses...)': '搜索模块（询价、用户、许可证...）',
  Search: '搜索',
  'No matching modules. Press Enter to search RFQs.': '没有匹配的模块。按 Enter 搜索询价。',
  'Refresh page': '刷新页面',
  Messages: '消息',
  Notifications: '通知',
  'Latest Notifications': '最新通知',
  All: '全部',
  Onboarding: '入驻',
  'Loading notifications...': '正在加载通知...',
  'No new notifications': '暂无新通知',
  "You're all caught up.": '已全部处理。',
  Close: '关闭',
  'Mark all as read': '全部标为已读',
  Profile: '个人资料',
  'Change/Reset Password': '更改/重置密码',
  'Language Selection': '语言选择',
  'Time Zone': '时区',
  'Date and Time Format': '日期和时间格式',
  'Help & Docs': '帮助与文档',
  'Toggle menu': '切换菜单',
  Admin: '管理',
  'Manage your account settings, preferences, and security options':
    '管理账户设置、偏好与安全选项',
  Display: '显示',
  Security: '安全',
  Procurement: '采购流程',
  'Banking Details': '银行信息',
  'Display Preferences': '显示偏好',
  'Customize how information is displayed in your portal': '自定义门户中信息的显示方式',
  Theme: '主题',
  Light: '浅色',
  Dark: '深色',
  'Auto (System)': '自动（系统）',
  Language: '语言',
  English: '英语',
  Spanish: '西班牙语',
  French: '法语',
  German: '德语',
  Chinese: '中文',
  'Date Format': '日期格式',
  'Time Format': '时间格式',
  '12 Hour (AM/PM)': '12 小时（上午/下午）',
  '24 Hour': '24 小时',
  Currency: '货币',
  'Enable Shipment Map': '启用货运地图',
  'Display the interactive tracking map on shipment detail pages': '在货运详情页显示交互式跟踪地图',
  'Apply Display Preferences': '应用显示偏好',
  'Display preferences applied': '显示偏好已应用',
  'Display currency updated across the portal': '门户货币已更新',
  'Language updated across the portal': '门户语言已更新',
  'Total RFQs Received': '收到的询价',
  'Total Merchants': '商户总数',
  'Total Happy Customers': '客户总数',
  'Total Brands': '品牌总数',
  'Total Items Send': '已发物品',
  'Total Categories': '类别总数',
  'Updated Now': '刚刚更新',
  'Invite Admin': '邀请管理员',
  'Bar Chart - Multiple': '柱状图',
  'Area Chart - Gradient': '面积图',
  'Showing RFQs for the last 6 months': '显示最近 6 个月的询价',
  'Showing total visitors for the last 6 months': '显示最近 6 个月的访问量',
  'Admin Portal': '管理门户',
  'Admin Sign In': '管理员登录',
  'Access the administration dashboard': '进入管理仪表板',
  Email: '电子邮件',
  Password: '密码',
  'Forgot password?': '忘记密码？',
  'Sign In': '登录',
  'Signing in...': '正在登录...',
  'Choose your preferred language for the interface': '选择界面语言',
  'Select Language': '选择语言',
  'Save Language': '保存语言',
  'Language preference saved successfully': '语言偏好已保存',
  Saving: '保存中',
  'Sales · RFQ': '销售 · 询价',
  'Sales · Offers': '销售 · 报价',
  'Sales · Purchase Orders': '销售 · 采购订单',
  'Sales · In Progress': '销售 · 进行中',
  'Sales · Order History': '销售 · 订单历史',
  'Sales · Claims': '销售 · 索赔',
  'Purchases · RFQ': '采购 · 询价',
  'Purchases · Offers': '采购 · 报价',
  'Purchases · Purchase Orders': '采购 · 采购订单',
  'Purchases · In Progress': '采购 · 进行中',
  'Purchases · Order History': '采购 · 订单历史',
  'Purchases · Claims': '采购 · 索赔',
  'Vessel Database': '船舶数据库',
  'Data Management': '数据管理',
};

const it: Dict = {
  Settings: 'Impostazioni',
  Display: 'Visualizzazione',
  Language: 'Lingua',
  Dashboard: 'Cruscotto',
  Logout: 'Esci',
  Notifications: 'Notifiche',
  Security: 'Sicurezza',
  Sales: 'Vendite',
  Purchases: 'Acquisti',
  Users: 'Utenti',
  'Apply Display Preferences': 'Applica preferenze',
  'Language updated across the portal': 'Lingua aggiornata in tutto il portale',
  'Sign In': 'Accedi',
  'Save Language': 'Salva lingua',
};

const pt: Dict = {
  Settings: 'Definições',
  Display: 'Exibição',
  Language: 'Idioma',
  Dashboard: 'Painel',
  Logout: 'Sair',
  Notifications: 'Notificações',
  Security: 'Segurança',
  Sales: 'Vendas',
  Purchases: 'Compras',
  Users: 'Utilizadores',
  'Apply Display Preferences': 'Aplicar preferências',
  'Language updated across the portal': 'Idioma atualizado em todo o portal',
  'Sign In': 'Entrar',
  'Save Language': 'Guardar idioma',
};

const nl: Dict = {
  Settings: 'Instellingen',
  Display: 'Weergave',
  Language: 'Taal',
  Dashboard: 'Dashboard',
  Logout: 'Uitloggen',
  Notifications: 'Meldingen',
  Security: 'Beveiliging',
  Sales: 'Verkoop',
  Purchases: 'Inkoop',
  Users: 'Gebruikers',
  'Apply Display Preferences': 'Voorkeuren toepassen',
  'Language updated across the portal': 'Taal bijgewerkt in het hele portaal',
  'Sign In': 'Inloggen',
  'Save Language': 'Taal opslaan',
};

const pl: Dict = {
  Settings: 'Ustawienia',
  Display: 'Wyświetlanie',
  Language: 'Język',
  Dashboard: 'Pulpit',
  Logout: 'Wyloguj',
  Notifications: 'Powiadomienia',
  Security: 'Bezpieczeństwo',
  Sales: 'Sprzedaż',
  Purchases: 'Zakupy',
  Users: 'Użytkownicy',
  'Apply Display Preferences': 'Zastosuj preferencje',
  'Language updated across the portal': 'Język zaktualizowany w całym portalu',
  'Sign In': 'Zaloguj się',
  'Save Language': 'Zapisz język',
};

const ru: Dict = {
  Settings: 'Настройки',
  Display: 'Отображение',
  Language: 'Язык',
  Dashboard: 'Панель',
  Logout: 'Выйти',
  Notifications: 'Уведомления',
  Security: 'Безопасность',
  Sales: 'Продажи',
  Purchases: 'Закупки',
  Users: 'Пользователи',
  'Apply Display Preferences': 'Применить настройки',
  'Language updated across the portal': 'Язык обновлён во всём портале',
  'Sign In': 'Войти',
  'Save Language': 'Сохранить язык',
};

export const TRANSLATIONS: Record<LanguageCode, Dict> = {
  en: {},
  es,
  fr,
  de,
  zh,
  it,
  pt,
  nl,
  pl,
  ru,
};

export function isSupportedLanguage(code: string): code is LanguageCode {
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(code);
}

export function translate(language: string, key: string): string {
  const code = isSupportedLanguage(language) ? language : 'en';
  return TRANSLATIONS[code][key] ?? TRANSLATIONS.en[key] ?? key;
}
